import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Typography,
  Button,
  TextField,
  Container,
  Paper,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import { API } from './config';
import { getApiErrorMessage } from './utils/apiError';

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const ForgotPassword = () => {
  const location = useLocation();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const role = String(query.get('role') || '').toLowerCase();
  const initialEmail = query.get('email') || '';

  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const isCourier =
    role === 'courier' ||
    role === 'driver' ||
    role === 'carrier';

  const signInPath = isCourier ? '/courier/sign-in' : '/customer/sign-in';

  const title = isCourier
    ? 'Driver / Carrier Password Reset'
    : 'Customer Password Reset';

  const subtitle =
    'Enter your account email and we will send a password reset link if the email exists.';

  const handleSubmit = async (e) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    setMessage('');
    setError('');

    if (!isValidEmail(normalizedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(
          getApiErrorMessage(
            data,
            'Unable to send password reset link. Please try again.'
          )
        );
        return;
      }

      setMessage('If this email exists, a reset link has been sent.');
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        py: { xs: 4, md: 8 },
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          px: { xs: 2.25, sm: 3 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3.5, md: 5 },
            borderRadius: 4,
            border: '1px solid rgba(15, 23, 42, 0.08)',
            maxWidth: 620,
            mx: 'auto',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              mb: 3,
              maxWidth: 540,
              mx: 'auto',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.95rem', sm: '2.2rem', md: '2.35rem' },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.035em',
                color: '#0f172a',
                mb: 1,
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                maxWidth: 500,
                mx: 'auto',
                lineHeight: 1.55,
                mb: 3,
                fontSize: { xs: '0.98rem', sm: '1rem' },
              }}
            >
              {subtitle}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.25,
                maxWidth: 430,
                mx: 'auto',
                mb: 2,
              }}
            >
              <Button
                component={Link}
                to={signInPath}
                variant="outlined"
                color="primary"
                fullWidth
                sx={{
                  minHeight: 44,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.88rem', sm: '0.86rem', md: '0.9rem' },
                  letterSpacing: '0.025em',
                }}
              >
                BACK TO SIGN IN
              </Button>

              <Button
                component={Link}
                to="/"
                variant="text"
                color="primary"
                fullWidth
                sx={{
                  minHeight: 44,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.88rem', sm: '0.86rem', md: '0.9rem' },
                  letterSpacing: '0.025em',
                }}
              >
                BACK TO HOME
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {message && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {message}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <TextField
              label="Email"
              type="text"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputProps={{
                inputMode: 'email',
                autoCapitalize: 'none',
                spellCheck: false,
              }}
              required
              sx={{ mb: 2 }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={submitting}
              sx={{
                mt: 1,
                minHeight: 46,
                fontWeight: 800,
                textTransform: 'uppercase',
                borderRadius: 2,
                letterSpacing: '0.025em',
              }}
            >
              {submitting ? 'SENDING...' : 'SEND RESET LINK'}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword;